/**
 * 번들(tar.gz)을 내려받아 꽂아 둔 USB 폴더에 바로 풀어쓴다.
 *
 * 기존 절차는 "내려받기 → 압축 풀기 → 터미널에서 write-usb.sh" 3단계였다.
 * 브라우저 내장 기능만으로 그 셋을 없앨 수 있다 — gzip 해제는
 * DecompressionStream, 폴더 쓰기는 File System Access API. 새 의존성도,
 * 서버 변경도 없다. 스트리밍이라 수 GB 도커 이미지도 메모리에 다 올리지 않는다.
 *
 * 브라우저는 USB 를 자동으로 잡지 못한다(OS 보안). 사용자가 USB 폴더를 한 번
 * 고르면 그 뒤로는 전부 자동이다.
 */

// TS 기본 lib 에 아직 없는 두 웹 표준의 최소 선언. 쓰는 만큼만 적는다
// (전역 타입 패키지를 추가하면 다른 코드의 타입까지 흔들린다).
declare global {
  interface Window {
    showDirectoryPicker(options?: { mode?: 'read' | 'readwrite' }): Promise<FileSystemDirectoryHandle>
  }
  interface FileSystemFileHandle {
    createWritable(): Promise<FileSystemWritableFileStream>
  }
  interface FileSystemWritableFileStream {
    write(data: BufferSource | Blob | string): Promise<void>
    close(): Promise<void>
  }
  class DecompressionStream {
    constructor(format: 'gzip' | 'deflate' | 'deflate-raw')
    readonly readable: ReadableStream<Uint8Array>
    readonly writable: WritableStream<Uint8Array>
  }
}

/** tar 블록 크기 — 헤더도 파일 데이터도 이 배수로 정렬된다. */
const BLOCK = 512

/** 한 번에 USB 로 흘려보내는 최대 크기. */
const WRITE_CHUNK = 4 * 1024 * 1024

export interface UsbWriteProgress {
  /** 방금 쓴 번들 내 경로 */
  path: string
  /** 지금까지 쓴 파일 수 */
  files: number
  /** 지금까지 쓴 바이트 (압축 해제 기준) */
  bytes: number
}

/** File System Access API 를 쓸 수 있는 브라우저인가 (Chrome/Edge). */
export function canWriteToUsb(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof (window as { showDirectoryPicker?: unknown }).showDirectoryPicker === 'function' &&
    typeof DecompressionStream === 'function'
  )
}

/**
 * tar 숫자 필드. 보통 8진수 문자열이지만 8GiB 를 넘는 크기는 최상위 비트를 세운
 * base-256 으로 들어온다 — 도커 이미지 tar 가 그 크기가 될 수 있어 둘 다 읽는다.
 */
function readNumber(block: Uint8Array, offset: number, length: number): number {
  if (block[offset] & 0x80) {
    let value = 0
    for (let i = offset + 1; i < offset + length; i += 1) value = value * 256 + block[i]
    return value
  }
  let text = ''
  for (let i = offset; i < offset + length; i += 1) {
    const c = block[i]
    if (c === 0 || c === 0x20) break
    text += String.fromCharCode(c)
  }
  return text === '' ? 0 : parseInt(text, 8)
}

function readString(block: Uint8Array, offset: number, length: number): string {
  let end = offset
  while (end < offset + length && block[end] !== 0) end += 1
  return new TextDecoder().decode(block.subarray(offset, end))
}

/** 바이트 스트림을 요청한 길이만큼씩 잘라 주는 리더. */
function chunkReader(stream: ReadableStream<Uint8Array>) {
  const reader = stream.getReader()
  let buffer = new Uint8Array(0)
  let finished = false

  const pull = async (): Promise<boolean> => {
    if (finished) return false
    const { value, done } = await reader.read()
    if (done || !value) {
      finished = true
      return false
    }
    const next = new Uint8Array(buffer.length + value.length)
    next.set(buffer)
    next.set(value, buffer.length)
    buffer = next
    return true
  }

  return {
    /** 정확히 n 바이트. 스트림이 먼저 끝나면 null. */
    async read(n: number): Promise<Uint8Array | null> {
      if (n === 0) return new Uint8Array(0)
      while (buffer.length < n) {
        if (!(await pull())) return null
      }
      const out = buffer.subarray(0, n)
      buffer = buffer.subarray(n)
      return out
    },
  }
}

/** 중첩 경로를 따라 디렉터리를 만들며 내려간다. */
async function ensureDir(
  root: FileSystemDirectoryHandle,
  segments: string[]
): Promise<FileSystemDirectoryHandle> {
  let dir = root
  for (const segment of segments) {
    dir = await dir.getDirectoryHandle(segment, { create: true })
  }
  return dir
}

/** `..` 이나 절대경로가 섞인 tar 로 고른 폴더 밖에 쓰지 않게 한다. */
function safeSegments(path: string): string[] | null {
  const segments = path.split('/').filter(s => s !== '' && s !== '.')
  if (segments.some(s => s === '..')) return null
  return segments
}

const NUL = String.fromCharCode(0)

/**
 * gzip tar 스트림을 dir 아래에 그대로 풀어쓴다.
 *
 * @returns 쓴 파일 수
 */
export async function extractTarGz(
  body: ReadableStream<Uint8Array>,
  dir: FileSystemDirectoryHandle,
  onProgress?: (p: UsbWriteProgress) => void
): Promise<number> {
  const reader = chunkReader(body.pipeThrough(new DecompressionStream('gzip')))
  let files = 0
  let bytes = 0
  // PAX(x) / GNU(L) 확장 헤더가 다음 엔트리의 긴 경로를 들고 온다.
  let pendingName: string | null = null

  for (;;) {
    const header = await reader.read(BLOCK)
    if (!header) break
    // 이름 첫 바이트가 0 이면 끝 표식 블록.
    if (header[0] === 0) break

    const type = String.fromCharCode(header[156] || 0x30)
    const size = readNumber(header, 124, 12)
    const padded = Math.ceil(size / BLOCK) * BLOCK

    let name = pendingName ?? readString(header, 0, 100)
    if (pendingName === null) {
      const prefix = readString(header, 345, 155)
      if (prefix) name = `${prefix}/${name}`
    }
    pendingName = null

    if (type === 'x' || type === 'g' || type === 'L') {
      // 확장 헤더 본문에서 경로를 꺼내 다음 엔트리에 쓴다.
      const payload = await reader.read(padded)
      if (!payload) break
      const text = new TextDecoder().decode(payload.subarray(0, size))
      if (type === 'L') {
        pendingName = text.split(NUL)[0]
      } else {
        const match = /(?:^|\n)\d+ path=([^\n]*)\n/.exec(text)
        if (match) pendingName = match[1]
      }
      continue
    }

    const segments = safeSegments(name)
    if (!segments || segments.length === 0) {
      await reader.read(padded)
      continue
    }

    if (type === '5') {
      await ensureDir(dir, segments)
      continue
    }
    if (type !== '0' && type !== NUL && type !== ' ') {
      // 심볼릭 링크 등은 USB(exFAT)로 옮길 수 없다 — 건너뛴다.
      await reader.read(padded)
      continue
    }

    const fileName = segments[segments.length - 1]
    const parent = await ensureDir(dir, segments.slice(0, -1))
    const handle = await parent.getFileHandle(fileName, { create: true })
    const writable = await handle.createWritable()
    try {
      // 큰 파일도 블록 단위로 흘려보낸다 — 통째로 메모리에 올리지 않는다.
      let remaining = size
      while (remaining > 0) {
        const want = Math.min(remaining, WRITE_CHUNK)
        const chunk = await reader.read(Math.ceil(want / BLOCK) * BLOCK)
        if (!chunk) throw new Error(`번들이 도중에 끊겼습니다: ${name}`)
        const take = Math.min(remaining, chunk.length)
        await writable.write(chunk.subarray(0, take))
        remaining -= take
        bytes += take
      }
      // 마지막 블록의 패딩만큼 버린다.
      const leftover = padded - Math.ceil(size / BLOCK) * BLOCK
      if (leftover > 0) await reader.read(leftover)
    } finally {
      await writable.close()
    }
    files += 1
    onProgress?.({ path: name, files, bytes })
  }

  return files
}

/**
 * USB 폴더를 고르고 번들을 그 안 `folder/` 에 풀어쓴다 — 공통 킷은 `luzer-kit`,
 * 현장 패키지는 `luzer-{현장코드}`. 사용자가 고르기를 취소하면 null.
 */
export async function writeKitToUsb(
  downloadUrl: string,
  folder: string,
  onProgress?: (p: UsbWriteProgress) => void
): Promise<{ folder: string; files: number } | null> {
  let root: FileSystemDirectoryHandle
  try {
    root = await window.showDirectoryPicker({ mode: 'readwrite' })
  } catch (e) {
    // 사용자가 선택을 취소한 경우 — 오류로 다루지 않는다.
    if (e instanceof DOMException && e.name === 'AbortError') return null
    throw e
  }

  const response = await fetch(downloadUrl)
  if (!response.ok || !response.body) {
    throw new Error(`번들을 내려받지 못했습니다 (HTTP ${response.status}).`)
  }

  const target = await root.getDirectoryHandle(folder, { create: true })
  const files = await extractTarGz(response.body, target, onProgress)
  return { folder, files }
}
